import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { applyPageSeo } from "../lib/seo";
import { BILLING_TIERS, type BillingOverview, type BillingPlan } from "../../shared/billing";
import { authClient } from "../lib/auth-client";
import {
  BILLING_PORTAL_PATH,
  createCheckoutSession,
  fetchBillingStatus,
} from "../lib/billing";
import { trackPricingPageViewed, trackPricingCtaClicked, trackCheckoutStarted } from "../lib/analytics";

const comparisonFeatures = [
  { name: "AI generations", free: "3 / day", pro: "200 / day" },
  { name: "Exports", free: "5 / month", pro: "500 / month" },
  { name: "Saved drafts", free: "10", pro: "300" },
  { name: "Template library", free: "148+ templates", pro: "148+ templates" },
  { name: "AI email generation", free: true, pro: true },
  { name: "HTML export", free: true, pro: true },
  { name: "React Email source export", free: false, pro: true },
  { name: "Priority email support", free: false, pro: true },
];

const faqItems = [
  {
    question: "What happens when I hit my generation limit?",
    answer:
      "On the Free plan, you'll see a prompt to view available plans. When checkout is available, upgrade to Pro for 200 generations per day and 500 exports per month.",
  },
  {
    question: "Can I cancel my subscription at any time?",
    answer:
      "If you subscribe to Pro, you can manage cancellation through the Stripe billing portal. The portal shows when cancellation takes effect.",
  },
  {
    question: "Do you offer refunds?",
    answer:
      "Any voluntary refund period will be shown at checkout. Unless otherwise stated at purchase, charges are non-refundable. Your statutory rights are unaffected.",
  },
  {
    question: "What export formats are supported?",
    answer:
      "All plans support HTML export. Pro additionally supports React Email component export, which integrates directly with modern email sending infrastructure.",
  },
];

interface BillingConfig {
  checkoutEnabled: boolean;
  plans: string[];
}

export function Pricing() {
  useEffect(() => {
    const cleanup = applyPageSeo({
      title: "Pricing",
      description:
        "Simple, transparent pricing. Start free and upgrade to Pro for 200 AI generations per day and 500 exports per month, when checkout is available.",
      path: "/pricing",
      keywords: ["react email builder pricing", "email template pricing", "AI email builder plans"],
    });

    // Add FAQ structured data for pricing page rich results
    const pricingFaqJsonLd = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": [
        { "@type": "Question", "name": "Is the free tier really free?", "acceptedAnswer": { "@type": "Answer", "text": "Yes. The free tier gives you 3 AI generations per day, 5 exports per month, and 10 saved drafts. No credit card required." } },
        { "@type": "Question", "name": "Can I cancel my paid plan?", "acceptedAnswer": { "@type": "Answer", "text": "If you subscribe to Pro, you can manage cancellation through the Stripe billing portal. The portal shows when cancellation takes effect." } },
        { "@type": "Question", "name": "What formats can I export?", "acceptedAnswer": { "@type": "Answer", "text": "All plans can export as HTML. Pro also exports React Email JSX source code for integration into your codebase." } },
        { "@type": "Question", "name": "Do I need to know React?", "acceptedAnswer": { "@type": "Answer", "text": "No. The AI builder generates templates from plain-language descriptions. Export as HTML works with any email provider. React source export is available for developers." } },
      ],
    };
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.textContent = JSON.stringify(pricingFaqJsonLd);
    script.id = "pricing-faq-jsonld";
    document.head.appendChild(script);

    return () => {
      cleanup();
      const existing = document.getElementById("pricing-faq-jsonld");
      if (existing) existing.remove();
    };
  }, []);
  useEffect(() => {
    trackPricingPageViewed();
  }, []);

  const navigate = useNavigate();
  const { data: session, isPending: isSessionPending } = authClient.useSession();
  const [billingOverview, setBillingOverview] = useState<BillingOverview | null>(null);
  const [billingLoading, setBillingLoading] = useState(false);
  const [billingError, setBillingError] = useState<string | null>(null);
  const [activePlan, setActivePlan] = useState<BillingPlan | null>(null);
  const [billingConfig, setBillingConfig] = useState<BillingConfig | null>(null);
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [showComparison, setShowComparison] = useState(false);

  // Fetch public billing config (no auth required)
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const res = await fetch("/api/billing/config");
        if (res.ok && !cancelled) {
          setBillingConfig(await res.json());
        }
      } catch {
        // leave null — checkout assumed unavailable
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;

    if (!session) {
      setBillingOverview(null);
      setBillingLoading(false);
      setBillingError(null);
      return undefined;
    }

    setBillingLoading(true);

    void (async () => {
      try {
        const response = await fetchBillingStatus();

        if (!cancelled) {
          setBillingOverview(response);
          setBillingError(null);
        }
      } catch (error) {
        if (!cancelled) {
          setBillingError(
            error instanceof Error ? error.message : "Unable to load billing details.",
          );
        }
      } finally {
        if (!cancelled) {
          setBillingLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [session]);

  // Auto-trigger checkout when landing on /pricing?checkout=pro after signup redirect
  const [searchParams, setSearchParams] = useSearchParams();
  const checkoutPlan = searchParams.get("checkout") as BillingPlan | null;
  const [showCheckoutCanceled, setShowCheckoutCanceled] = useState(
    searchParams.get("checkout") === "canceled",
  );

  useEffect(() => {
    if (!checkoutPlan || !session || billingLoading) return;
    if (checkoutPlan !== "pro") return;

    // Clear the param so it doesn't re-trigger
    const timer = setTimeout(() => {
      handleTierAction(checkoutPlan);
    }, 500);

    return () => clearTimeout(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session, checkoutPlan, billingLoading]);

  async function handleTierAction(plan: BillingPlan) {
    if (plan === "free") {
      navigate(session ? "/dashboard" : "/auth/signup?redirect=%2Fdashboard");
      return;
    }

    if (!session) {
      navigate(`/auth/signup?redirect=${encodeURIComponent(`/pricing?checkout=${plan}`)}`);
      return;
    }

    trackPricingCtaClicked({ plan: plan, location: "pricing_page" });
    setActivePlan(plan);
    setBillingError(null);

    try {
      if (billingOverview?.billing.isPaid) {
        window.location.assign(BILLING_PORTAL_PATH);
        return;
      }

      trackCheckoutStarted({ plan: plan });
      const response = await createCheckoutSession(plan);
      window.location.assign(response.url);
    } catch (error) {
      setBillingError(
        error instanceof Error ? error.message : "Unable to start checkout right now.",
      );
    } finally {
      setActivePlan(null);
    }
  }

  const checkoutEnabled = billingConfig?.checkoutEnabled ?? false;

  return (
    <section className="app-surface relative overflow-hidden py-12 sm:py-16">
      <div aria-hidden="true" className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-sky-300/70 to-transparent" />
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 bg-grid-fade opacity-50" />
      <div aria-hidden="true" className="absolute -left-32 top-12 h-72 w-72 rounded-full bg-sky-300/20 blur-[120px]" />
      <div aria-hidden="true" className="absolute -right-24 -top-12 h-72 w-72 rounded-full bg-indigo-300/15 blur-[120px]" />
      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="text-center">
          <p className="eyebrow">Pricing</p>
          <h1 className="mt-3 text-3xl font-semibold tracking-tight text-slate-950 sm:text-4xl">
            Simple, transparent pricing
          </h1>
          <p className="mx-auto mt-3 max-w-2xl text-base leading-7 text-slate-600 sm:text-lg sm:leading-8">
            Start free{checkoutEnabled ? " and upgrade to Pro when you need more. No hidden fees, cancel anytime." : ". Pro plan available soon."}
          </p>
        </div>

        {showCheckoutCanceled ? (
          <div
            data-testid="checkout-canceled-banner"
            className="mx-auto mt-8 flex items-start gap-3 rounded border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800"
          >
            <svg aria-hidden="true" className="mt-0.5 h-5 w-5 shrink-0 text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div className="flex-1">
              <p className="font-semibold">Checkout was canceled</p>
              <p className="mt-0.5 text-sm text-amber-700">
                No charge was made. You can try again whenever you're ready.
              </p>
            </div>
            <button
              type="button"
              onClick={() => {
                setShowCheckoutCanceled(false);
                searchParams.delete("checkout");
                setSearchParams(searchParams, { replace: true });
              }}
              className="ml-2 shrink-0 text-amber-400 transition hover:text-amber-600"
              aria-label="Dismiss"
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        ) : null}

        {billingError ? (
          <div className="mx-auto mt-8 max-w-2xl rounded border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
            {billingError}
          </div>
        ) : null}

        <div className="mt-8 grid gap-4 lg:grid-cols-2 lg:max-w-3xl lg:mx-auto">
          {BILLING_TIERS.map((tier) => {
            const currentPlan = billingOverview?.billing.plan;
            const isCurrentPlan = currentPlan === tier.id;
            const isBusy = activePlan === tier.id;
            const isPaidSubscriber = Boolean(session && billingOverview?.billing.isPaid);
            const isProTier = tier.id === "pro";
            const isDisabled =
              isBusy ||
              (tier.id !== "free" && isCurrentPlan) ||
              isSessionPending ||
              (Boolean(session) && billingLoading);

            const buttonLabel =
              tier.id === "free"
                ? session
                  ? "Go to Dashboard"
                  : tier.cta
                : isCurrentPlan
                  ? "Current Plan"
                  : isPaidSubscriber
                    ? "Manage in Billing Portal"
                    : checkoutEnabled
                      ? tier.cta
                      : "Coming Soon";

            return (
              <div
                key={tier.name}
                className={`relative flex flex-col rounded-lg bg-white/95 p-6 shadow-card backdrop-blur transition hover:-translate-y-0.5 hover:shadow-elevated ${
                  tier.highlighted
                    ? "ring-2 ring-sky-500"
                    : "ring-1 ring-slate-200/80"
                }`}
              >
                {tier.highlighted && checkoutEnabled && (
                  <span className="absolute -top-3.5 left-1/2 -translate-x-1/2 rounded bg-sky-500 px-4 py-1 text-xs font-semibold text-white">
                    Most Popular
                  </span>
                )}

                <h3 className="text-lg font-semibold text-slate-950">
                  {tier.name}
                </h3>
                <p className="mt-1 text-sm text-slate-500">{tier.description}</p>

                <div className="mt-4 flex items-baseline gap-1">
                  <span className="text-3xl font-extrabold tracking-tight text-slate-950">
                    {tier.priceLabel}
                  </span>
                  <span className="text-sm font-medium text-slate-500">
                    {tier.periodLabel}
                  </span>
                </div>

                <ul className="mt-6 flex-1 space-y-3">
                  {tier.features.map((feature) => (
                    <li key={feature} className="flex items-start gap-3">
                      <svg
                        aria-hidden="true"
                        className="mt-0.5 h-5 w-5 shrink-0 text-sky-500"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                      <span className="text-sm text-slate-600">{feature}</span>
                    </li>
                  ))}
                </ul>

                {isProTier && !checkoutEnabled ? (
                  <div className="mt-4 rounded border border-amber-200 bg-amber-50 px-4 py-2.5 text-xs text-amber-700">
                    Online checkout is being configured.{" "}
                    <a
                      href="mailto:hello@reactemailbuilder.com?subject=Pro%20plan%20early%20access"
                      className="font-semibold underline hover:text-amber-900"
                    >
                      Contact us
                    </a>{" "}to upgrade early.
                  </div>
                ) : null}

                <button
                  type="button"
                  onClick={() => void handleTierAction(tier.id)}
                  disabled={isDisabled || (isProTier && !checkoutEnabled && !isCurrentPlan && !isPaidSubscriber)}
                  className={`mt-6 block rounded px-5 py-2.5 text-center text-sm font-semibold transition ${
                    tier.highlighted
                      ? "bg-sky-500 text-white hover:bg-sky-600"
                      : "bg-white text-slate-700 ring-1 ring-slate-300 hover:bg-slate-50"
                  } disabled:cursor-not-allowed disabled:opacity-70`}
                >
                  {isBusy ? "Redirecting..." : billingLoading && session ? "Loading..." : buttonLabel}
                </button>
              </div>
            );
          })}
        </div>

        {/* Competitive advantage — free tier vs paid-only tools */}
        <div data-testid="free-tier-advantage" className="mx-auto mt-8 max-w-2xl rounded border border-emerald-200 bg-emerald-50 px-5 py-4 sm:px-6 sm:py-5">
          <div className="flex items-start gap-4">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded bg-emerald-100">
              <svg aria-hidden="true" className="h-5 w-5 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
            </div>
            <div>
              <p className="text-sm font-semibold text-emerald-900">
                Most email template tools charge from day one. We don't.
              </p>
              <p className="mt-1 text-sm leading-6 text-emerald-700">
3 AI generations/day, 5 HTML exports/month, 148+ templates — no card, no trial. Create an account to generate and save.
              </p>
            </div>
          </div>
        </div>

        {/* Trust bar — reduces purchase anxiety (only shown when checkout is live) */}
        {checkoutEnabled ? (
          <div className="mt-10 flex flex-wrap items-center justify-center gap-x-8 gap-y-3 text-sm text-slate-500">
            <div className="inline-flex items-center gap-2">
              <svg aria-hidden="true" className="h-4 w-4 text-emerald-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
              Secure checkout via Stripe
            </div>
            <div className="inline-flex items-center gap-2">
              <svg aria-hidden="true" className="h-4 w-4 text-emerald-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
              Cancellation managed in Stripe
            </div>
          </div>
        ) : null}

        {/* Feature comparison table */}
        <div className="mx-auto mt-14 max-w-5xl">
          <p className="text-center text-sm font-semibold uppercase tracking-[0.24em] text-sky-700">
            Feature comparison
          </p>
          <h2 className="mt-3 text-center text-2xl font-semibold tracking-tight text-slate-950 sm:text-3xl">
            Compare plans
          </h2>
          <p className="mx-auto mt-2 max-w-xl text-center text-base text-slate-600">
All plans include the full template library.{checkoutEnabled ? " Upgrade for higher limits." : " Pro plan coming soon for higher limits."}
          </p>
          {/* Mobile: show toggle; desktop: always visible */}
          <button
            type="button"
            onClick={() => setShowComparison(!showComparison)}
            className="mx-auto mt-8 flex items-center gap-2 rounded border border-slate-300 bg-white px-5 py-2.5 text-sm font-medium text-slate-700 transition hover:bg-slate-50 lg:hidden"
          >
            {showComparison ? "Hide" : "Show"} feature comparison
            <svg aria-hidden="true" className={`h-4 w-4 text-slate-400 transition-transform ${showComparison ? "rotate-180" : ""}`} viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clipRule="evenodd" />
            </svg>
          </button>
          <div className={`mt-8 overflow-hidden rounded border border-slate-200/80 bg-white/90 shadow-[0_24px_64px_rgba(15,23,42,0.07)] backdrop-blur lg:mt-10 ${showComparison ? "block" : "hidden lg:block"}`}>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[480px] border-collapse text-sm">
                <thead>
                  <tr className="border-b border-slate-200 bg-slate-50/80">
                    <th className="py-4 pr-6 pl-6 text-left text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Feature</th>
                    <th className="px-6 py-4 text-center text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Free</th>
                    <th className="px-6 py-4 text-center text-xs font-semibold uppercase tracking-[0.18em] text-sky-600">Pro</th>
                  </tr>
                </thead>
                <tbody>
                  {comparisonFeatures.map((feature, i) => (
                    <tr
                      key={feature.name}
                      className={`border-b border-slate-100 ${i % 2 === 0 ? "bg-white" : "bg-slate-50/60"}`}
                    >
                      <td className="py-3.5 pr-6 pl-6 text-sm font-medium text-slate-950">{feature.name}</td>
                      {(["free", "pro"] as const).map((plan) => {
                        const value = feature[plan];
                        return (
                          <td key={plan} className="px-6 py-3.5 text-center">
                            {typeof value === "boolean" ? (
                              value ? (
                                <svg aria-hidden="true" className="mx-auto h-5 w-5 text-sky-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                              ) : (
                                <svg aria-hidden="true" className="mx-auto h-5 w-5 text-slate-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                              )
                            ) : (
                              <span className={`text-sm ${plan === "pro" ? "font-semibold text-sky-600" : "text-slate-600"}`}>
                                {value}
                              </span>
                            )}
                          </td>
                        );
                    })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div className="mx-auto mt-14 max-w-3xl">
          <p className="text-center text-sm font-semibold uppercase tracking-[0.24em] text-sky-700">
            Common questions
          </p>
          <h2 className="mt-3 text-center text-2xl font-semibold tracking-tight text-slate-950 sm:text-3xl">
            Frequently asked questions
          </h2>
          <dl className="mt-10 divide-y divide-slate-200">
            {faqItems.map((item, index) => (
              <div key={item.question} className="py-6">
                <dt>
                  <button
                    type="button"
                    onClick={() => setOpenFaq(openFaq === index ? null : index)}
                    className="flex w-full items-start justify-between text-left"
                  >
                    <span className="text-lg font-semibold text-slate-950">
                      {item.question}
                    </span>
                    <span className="ml-6 flex shrink-0 items-center">
                      <svg
                        aria-hidden="true"
                        className={`h-5 w-5 text-slate-400 transition-transform ${openFaq === index ? "rotate-180" : ""}`}
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clipRule="evenodd" />
                      </svg>
                    </span>
                  </button>
                </dt>
                {openFaq === index && (
                  <dd className="mt-3 pr-12">
                    <p className="text-sm leading-7 text-slate-600">{item.answer}</p>
                  </dd>
                )}
              </div>
            ))}
          </dl>
        </div>
      </div>
    </section>
  );
}
